import { useState, useEffect, useCallback } from 'react';

// Returns the string `value` field of an app setting module
export function useAppSettingValue(moduleName) {
  const readValue = useCallback(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('app_setting') || '[]');
      const key = moduleName.trim().toLowerCase().replace(/\s+/g, '');
      const found = settings.find(
        (s) => s?.module?.trim().toLowerCase().replace(/\s+/g, '') === key
      );
      return found?.value || '';
    } catch {
      return '';
    }
  }, [moduleName]);

  const [value, setValue] = useState(() => readValue());

  useEffect(() => { setValue(readValue()); }, [moduleName, readValue]);

  useEffect(() => {
    const handler = () => setValue(readValue());
    window.addEventListener('app_setting_updated', handler);
    return () => window.removeEventListener('app_setting_updated', handler);
  }, [readValue]);

  return value;
}

// Returns a function that maps original material type name → alias (falls back to original)
export function useMaterialTypeAlias() {
  const raw = useAppSettingValue('Material Type Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

// Returns a function that maps an original sub-operation label → alias
// (falls back to the original label). Configured in Application Settings.
export function useSubOperationAlias() {
  const raw = useAppSettingValue('Sub-operation Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

// Returns a function that maps an original operation type name → alias
// (falls back to the original name). Configured in Application Settings.
export function useOperationTypeAlias() {
  const raw = useAppSettingValue('Operation Type Alias');

  return (originalName) => {
    if (!raw || !originalName) return originalName;
    try {
      const map = JSON.parse(raw);
      const alias = map[originalName];
      return alias && alias.trim() ? alias.trim() : originalName;
    } catch {
      return originalName;
    }
  };
}

// Sampling types configured in Application Settings. Each one stores a JSON value
// { sampled_by, sent_to, decided_by } that drives who samples, who tests (enters the
// result) and who decides OK / Not OK. The form reads it through useSamplingTypeConfig.
export const SAMPLING_TYPES = {
  inline: 'Inline Sampling',
  ipc: 'IPC Sampling',
  qc: 'QC Sampling',
};

// The three types offered on the sampling form: value stored on the recipe item,
// and the Application Setting module that defines its roles.
export const SAMPLING_TYPE_OPTIONS = [
  { value: 'INLINE', label: 'Inline', module: SAMPLING_TYPES.inline },
  { value: 'IPC', label: 'IPC', module: SAMPLING_TYPES.ipc },
  { value: 'QC', label: 'QC', module: SAMPLING_TYPES.qc },
];

export const SAMPLING_TYPE_DEFAULTS = {
  'Inline Sampling': { sampled_by: 'Production', sent_to: 'Production', decided_by: 'Production' },
  'IPC Sampling': { sampled_by: 'Production', sent_to: 'QC', decided_by: 'Production' },
  'QC Sampling': { sampled_by: 'QC', sent_to: 'QC', decided_by: 'QC' },
};

// Returns { sampled_by, sent_to, decided_by } for a sampling type module name
// (e.g. SAMPLING_TYPES.ipc), falling back to the defaults for any missing field.
export function useSamplingTypeConfig(moduleName) {
  const raw = useAppSettingValue(moduleName);
  const defaults = SAMPLING_TYPE_DEFAULTS[moduleName] || {};
  try {
    return { ...defaults, ...JSON.parse(raw || '{}') };
  } catch {
    return { ...defaults };
  }
}

export default function useAppSetting(moduleName, defaultEnabled = false) {
  const readValue = useCallback(() => {
    try {
      const settings = JSON.parse(localStorage.getItem('app_setting') || '[]');
      const key = moduleName.trim().toLowerCase().replace(/\s+/g, '');
      const found = settings.find(
        (s) => s?.module?.trim().toLowerCase().replace(/\s+/g, '') === key
      );
      return found ? found.status === true : defaultEnabled;
    } catch {
      return defaultEnabled;
    }
  }, [moduleName, defaultEnabled]);

  const [enabled, setEnabled] = useState(() => readValue());

  useEffect(() => {
    // Re-read immediately when moduleName changes
    setEnabled(readValue());
  }, [moduleName, readValue]);

  useEffect(() => {
    const handler = () => setEnabled(readValue());
    window.addEventListener('app_setting_updated', handler);
    return () => window.removeEventListener('app_setting_updated', handler);
  }, [readValue]);

  return enabled;
}
